import { useCallback, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { IRoadshowResponse } from '@/creditflowresearch/application/Roadshows/vo/IRoadshowsResponse';
import {
  ICollectionPayload,
  IRoadshowsFilters,
} from '@/creditflowresearch/application/Roadshows/vo/IRoadshowsPayload';
import { getRoadshowsPayload, getRoadshowsTileViewData } from '@/creditflowresearch/application/Roadshows/helpers';
import { initRoadshowsTileCollectionFilters } from '@/creditflowresearch/application/Roadshows/constants';
import { useSessionContext } from '@users/session/SessionContext';
import { IRoadshowsTileView } from '@/creditflowresearch/application/Roadshows/vo/IRoadshowsTileView';
import useFilter from '@/creditflowresearch/application/Roadshows/useFilter';
import useFetchCollection from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import { getContainer } from '@framework/di/getContainer';
import RoadshowRepository from '@/creditflowresearch/infrastructure/repository/RoadshowRepository';

const COUNT_ROWS_PORTION = 5;

interface IUseRoadshowsTileProps {
  totalCount: number,
  collection: IRoadshowResponse[],
  filters: IRoadshowsFilters,
  hasServerError?: boolean,
}

const useRoadshowsTile = ({
  totalCount: initTotalCount = 0,
  collection: initCollection = [],
  filters: initFilters,
  hasServerError = false,
}:IUseRoadshowsTileProps) => {
  const { currentUser: { email } } = useSessionContext();
  const container = getContainer();
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(!hasServerError);
  const [roadshowsCollection, setRoadshowsCollection] = useState<IRoadshowsTileView[]>(
    getRoadshowsTileViewData(initCollection.slice(0, COUNT_ROWS_PORTION), email),
  );
  const [bufferCollection, setBufferCollection] = useState<IRoadshowsTileView[]>(
    getRoadshowsTileViewData(initCollection.slice(COUNT_ROWS_PORTION), email),
  );
  const { filters } = useFilter(initFilters);
  const [collectionPayload, setCollectionPayload] = useState<Partial<ICollectionPayload>>({ filters });
  const [searchValue, setSearchValue] = useState<string>('');

  const fetchRoadshowsList = useCallback(async ({ filters, ...collectionFilters }: ICollectionPayload) => {
    const roadshowRepository = container.get<RoadshowRepository>(RoadshowRepository);
    const payload = getRoadshowsPayload(filters, collectionFilters);
    const { collection, totalCount } = await roadshowRepository.getAllRoadshows(payload);

    if (payload.paging.page === 1) {
      setRoadshowsCollection(getRoadshowsTileViewData(collection.slice(0, COUNT_ROWS_PORTION), email));
      setBufferCollection(getRoadshowsTileViewData(collection.slice(COUNT_ROWS_PORTION), email));
    } else {
      setBufferCollection(getRoadshowsTileViewData(collection, email));
    }

    return {
      collection,
      totalCount,
    };
  }, []);

  const {
    debouncedSearch,
    debouncedSearchString,
    paginate,
    softReset,
    page,
    totalCount,
    isFetching,
  } = useFetchCollection<IRoadshowResponse, ICollectionPayload>(
    fetchRoadshowsList,
    {
      enabled: !isFirstFetching,
      payload: collectionPayload as ICollectionPayload,
      initialValues: { collection: initCollection, totalCount: initTotalCount, ...initRoadshowsTileCollectionFilters },
    },
  );

  const loadMore = () => {
    const newCollection = roadshowsCollection.concat(bufferCollection.slice(0, COUNT_ROWS_PORTION));
    setRoadshowsCollection(newCollection);

    if (bufferCollection.length === COUNT_ROWS_PORTION && newCollection.length < totalCount) {
      paginate(page + 1);
    } else {
      setBufferCollection(bufferCollection.slice(COUNT_ROWS_PORTION));
    }
  };

  useEffect(() => {
    softReset();
    setSearchValue('');
    setCollectionPayload({ filters });
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    return () => setIsFirstFetching(false);
  }, [JSON.stringify(collectionPayload), page, debouncedSearchString]);

  return {
    isFetching,
    collection: roadshowsCollection,
    isMore: totalCount > roadshowsCollection.length,
    loadMore,
    searchValue,
    setSearchValue,
    debouncedSearch,
    isHideTile: !roadshowsCollection.length && isEmpty(debouncedSearchString),
  };
};

export default useRoadshowsTile;
